import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, listRows, updateRow } from "@/lib/adminApi";
import {
  TICKET_PRIORITY,
  TICKET_STATUS,
  metaFor,
  optionsFrom,
  type SupportTicket,
} from "@/lib/adminContent";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnGhost,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const TEAM = ["Divya Rao", "Lakshmi Prasanna", "Suresh DNV", "Unassigned"];

export default function AdminSupportTickets() {
  const [rows, setRows] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<SupportTicket>("support_tickets", "created_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load support tickets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (id: number, patch: Partial<SupportTicket>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("support_tickets", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const remove = async (id: number) => {
    const current = rows.find((row) => row.id === id);
    setRows((list) => list.filter((row) => row.id !== id));
    setSelectedId(null);
    try {
      await deleteRow("support_tickets", id);
      notify(`${current?.ticket_ref || "Ticket"} deleted.`);
    } catch {
      setError("We couldn't delete that ticket. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (priorityFilter !== "all" && row.priority !== priorityFilter) return false;
      if (!query) return true;
      return [row.ticket_ref, row.subject, row.requester_name || "", row.requester_email || "", row.category || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter, priorityFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<SupportTicket>[] = [
    {
      key: "subject",
      header: "Ticket",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.subject}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.ticket_ref} · {row.category || "General"}</p>
        </div>
      ),
    },
    { key: "requester", header: "Requester", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.requester_name || "—"}</span> },
    { key: "priority", header: "Priority", render: (row) => <StatusPill {...metaFor(TICKET_PRIORITY, row.priority)} /> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(TICKET_STATUS, row.status)} /> },
    { key: "assigned", header: "Assigned", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.assigned_to || "Unassigned"}</span> },
    { key: "created", header: "Raised", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.created_at)}</span> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Customers"
        title="Support Tickets"
        subtitle="Every guest question, request and issue in one queue — triage by priority, assign an owner and track it to resolution."
      />

      <StatCards
        stats={[
          { label: "Total tickets", value: rows.length, icon: "ri-customer-service-2-line", tone: "primary" },
          { label: "Open", value: rows.filter((row) => row.status === "open").length, icon: "ri-error-warning-line", tone: "accent" },
          { label: "In progress", value: rows.filter((row) => row.status === "in_progress").length, icon: "ri-loader-4-line", tone: "secondary" },
          { label: "Resolved", value: rows.filter((row) => row.status === "resolved").length, icon: "ri-check-double-line", tone: "primary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by reference, subject, requester…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(TICKET_STATUS)]} />
          <Select label="Priority" value={priorityFilter} onChange={setPriorityFilter} options={[{ value: "all", label: "All priorities" }, ...optionsFrom(TICKET_PRIORITY)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} tickets</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading tickets…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-customer-service-2-line" title={rows.length === 0 ? "No tickets yet" : "No tickets match"} message="Guest support tickets will appear here." />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelectedId(null)} title={selected?.subject || "Ticket"} subtitle={selected?.ticket_ref} footer={selected ? (
        <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
          <i className="ri-delete-bin-line"></i> Delete ticket
        </button>
      ) : null}>
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(TICKET_STATUS, selected.status)} />
              <StatusPill {...metaFor(TICKET_PRIORITY, selected.priority)} />
            </div>

            {selected.message ? (
              <div className="bg-background-100 border border-background-200 rounded-lg p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">Guest message</p>
                <p className="text-sm text-foreground-800 leading-relaxed">{selected.message}</p>
              </div>
            ) : null}

            <DetailList
              items={[
                { icon: "ri-user-line", label: "Requester", value: selected.requester_name || "—" },
                { icon: "ri-mail-line", label: "Email", value: selected.requester_email || "—" },
                { icon: "ri-price-tag-3-line", label: "Category", value: selected.category || "General" },
                { icon: "ri-links-line", label: "Related booking", value: selected.related_booking || "—" },
                { icon: "ri-calendar-line", label: "Raised", value: formatDate(selected.created_at) },
              ]}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value }, "Ticket status updated.")}
                  className={fieldInput}
                >
                  {optionsFrom(TICKET_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Priority">
                <select
                  value={selected.priority}
                  onChange={(event) => update(selected.id, { priority: event.target.value }, "Ticket priority updated.")}
                  className={fieldInput}
                >
                  {optionsFrom(TICKET_PRIORITY).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Assigned to">
              <select
                value={selected.assigned_to || "Unassigned"}
                onChange={(event) => update(selected.id, { assigned_to: event.target.value === "Unassigned" ? null : event.target.value }, "Ticket reassigned.")}
                className={fieldInput}
              >
                {TEAM.map((member) => (
                  <option key={member} value={member}>{member}</option>
                ))}
              </select>
            </Field>

            {selected.requester_email ? (
              <Field label="Contact">
                <a href={`mailto:${selected.requester_email}?subject=Re: ${encodeURIComponent(selected.subject)} (${selected.ticket_ref})`} className={btnGhost}>
                  <i className="ri-mail-send-line"></i> Reply by email
                </a>
              </Field>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}